import Dexie from "dexie"

// We use "HDMK_OFFLINE" to bypass previous schema corruption/clashes 
// and ensure a clean, unified start for all users.
const db = new Dexie("HDMK_OFFLINE")

// Version 1: Initial Unified Schema
db.version(1).stores({
  users: "&uid,fname,lname,role,verified",
  inspections:
    "&id,uid,status,started,fee,payment,date.started,date.ended,[uid+status],[uid+date.started]",
  layouts: "&id,author",
  narratives: "++key, id, uid, author, comment, [author+comment], [uid+id]",
  media:
    "&name,uid,id,lid,path,uploaded,[id+lid],[id+uploaded],[lid+uploaded],[id+name],flagged,summary,[id+summary],[id+lid+flagged],[id+lid+summary],[id+uid],[id+sectionID],[id+path]",
  reinspection_media:
    "&name,uid,id,lid,path,uploaded,[id+lid],[id+uploaded],[lid+uploaded],[id+name],[id+path]",
  logs: "++id,type",
  comments:
    "&id, uid, inspectionID, sectionID, itemID, commentID, path, summary, [inspectionID+uid], [sectionID+inspectionID], [itemID+inspectionID],[commentID+inspectionID],[inspectionID+summary],[inspectionID+path]",
  summaries:
    "&id, uid, inspectionID, sectionID, itemID, commentID , [inspectionID+uid], [sectionID+inspectionID], [itemID+inspectionID],[commentID+inspectionID]",
  reinspection_comments:
    "&lid, id, uid, section, sectionID, inspectionID, itemID, commentID, path, summary, [inspectionID+uid], [sectionID+inspectionID], [itemID+inspectionID],[commentID+inspectionID],[inspectionID+path]",
  progress: "&id, sectionID, [id+sectionID]",
  fourpoints: "&id",
})

export * from "./Inspection"
export * from "./Layout"
export * from "./Comment"
export * from "./FourPoint"
export * from "./Narrative"
export * from "./Media"
export * from "./Chat"

let reopeningPromise: Promise<void> | null = null

/**
 * Migrates data from the old "HDMK" database to the new "HDMK_OFFLINE" instance.
 * Runs once upon first initialization to prevent data loss for offline users.
 */
async function migrateOldData() {
  const MIGRATION_KEY = "hdmk_migration_v2_done"
  if (typeof window === "undefined" || localStorage.getItem(MIGRATION_KEY)) return

  try {
    const oldExists = await Dexie.exists("HDMK")
    if (!oldExists) {
      localStorage.setItem(MIGRATION_KEY, "true")
      return
    }

    console.log("Old database detected. Starting migration to HDMK_OFFLINE...")
    const oldDb = new Dexie("HDMK")
    await oldDb.open()

    // Tables we want to preserve
    const tables = [
      "inspections",
      "fourpoints",
      "media",
      "comments",
      "progress",
      "narratives",
      "layouts"
    ]

    for (const tableName of tables) {
      try {
        const oldTable = oldDb.table(tableName)
        const count = await oldTable.count()
        if (count > 0) {
          console.log(`Migrating ${count} records from ${tableName}...`)

          // Process in chunks of 50 to avoid memory crashes with large dataURIs
          const CHUNK_SIZE = 50
          for (let offset = 0; offset < count; offset += CHUNK_SIZE) {
            const records = await oldTable.offset(offset).limit(CHUNK_SIZE).toArray()
            await db.table(tableName).bulkPut(records)
          }
        }
      } catch (e) {
        console.warn(`Could not migrate table ${tableName} (it might not exist in old version):`, e)
      }
    }

    console.log("Database migration completed successfully.")
    localStorage.setItem(MIGRATION_KEY, "true")
    oldDb.close()
  } catch (e) {
    console.error("Migration failed:", e)
  }
}


export async function ensureDb() {
  if (reopeningPromise) return reopeningPromise

  if (db.isOpen()) {
    try {
      // Use "inspections" for the ping as it's a very common table
      await db.table("inspections").count()
      return
    } catch (e: any) {
      console.warn("DB ping failed nonostante isOpen() === true. Forcing reconnect:", e?.message)
    }
  }

  reopeningPromise = (async () => {
    try {
      try {
        if (db.isOpen()) db.close()
      } catch (_) { }

      await new Promise(res => setTimeout(res, 150))
      await db.open()
      console.log("Database re-opened successfully")

      // Run migration after successful open
      await migrateOldData()
    } catch (e: any) {
      console.error("Failed to reopen database:", e)
      throw e
    } finally {
      reopeningPromise = null
    }
  })()

  return reopeningPromise
}

export function isDbError(error: any) {
  if (!error) return false
  const msg = error.message?.toLowerCase() || ""
  const name = error.name || ""
  return (
    name === "DatabaseClosedError" ||
    name === "AbortError" ||
    msg.includes("connection to indexed database server lost") ||
    msg.includes("database connection was closed") ||
    msg.includes("idbdatabase.transaction") ||
    msg.includes("the connection to the database has been lost") ||
    msg.includes("interaction with the database failed")
  )
}

export default db
